/**
 * Wire contracts for the Portfolio screen — a keyset-paginated, filterable,
 * sortable inventory of the organization's modeled loans, plus a cheap org-level
 * summary for the header stats.
 *
 * The list is built to back a virtualized, infinite-scrolling table over a
 * potentially very large loan book: the client passes the opaque `nextCursor`
 * back to fetch the next page (cursor pagination — never offset), and pushes
 * search / readiness filters / sort to the server so the working set shrinks in
 * Postgres rather than the browser. Numeric money/score fields cross the wire as
 * strings to preserve Decimal precision; the client formats them.
 *
 * The sort vocabulary (`PortfolioSortKey`, direction) is DERIVED from the shared
 * `common/portfolio.ts` source of truth — the same constants the server query
 * keyset-paginates by — so the wire enum and the DB query cannot drift.
 */
import { z } from 'zod';
import type { ApiContract } from '../contracts';
import {
  PORTFOLIO_SORT_KEYS,
  PORTFOLIO_SORT_DIRECTIONS,
  type PortfolioSortKey,
  type PortfolioSortDirection,
} from '../portfolio';
import {
  INFERRED_TYPES,
  NUMERIC_SUBTYPES,
  VALUE_CLASSIFIABLE_FIELD_KEYS,
  VALUE_CLASSIFICATIONS,
} from '../uploader';
import { SegmentSchema } from '../segment';
import { ModelingDatasetStatusSchema } from './modeling';

/** Field value type + display subtype enums, mirroring the uploader sources of truth. */
export const FieldValueTypeSchema = z.enum(INFERRED_TYPES);
export const FieldValueSubtypeSchema = z.enum(NUMERIC_SUBTYPES).nullable();

/** Sort key enum, derived from the shared `PORTFOLIO_SORT_KEYS` source of truth. */
export const PortfolioSortKeySchema = z.enum(
  PORTFOLIO_SORT_KEYS as readonly [PortfolioSortKey, ...PortfolioSortKey[]],
);

/** Sort direction enum, derived from the shared `PORTFOLIO_SORT_DIRECTIONS`. */
export const PortfolioSortDirectionSchema = z.enum(
  PORTFOLIO_SORT_DIRECTIONS as readonly [PortfolioSortDirection, ...PortfolioSortDirection[]],
);

/**
 * Machine-readable error code for a segment-predicate read that exceeded the shared
 * statement timeout (Postgres 57014 on the count or the loan list). Carried as `code`
 * in the standard error envelope with HTTP 422 — a typed, expected outcome for a
 * pathologically expensive slice, never a generic 500. Clients branch on it to show a
 * "segment too large" state (keep the last good count, offer retry) and skip
 * auto-retrying a deterministic timeout.
 */
export const SEGMENT_TOO_LARGE_CODE = 'SEGMENT_TOO_LARGE';

/**
 * F100 — machine-readable error code for a segment TEXT/regex filter whose regular expression
 * is not valid in Postgres POSIX ARE (the engine that runs `~`/`~*`). Carried as `code` in the
 * standard error envelope with HTTP 422 on the loan list, the count, AND the modeling curves,
 * so a malformed pattern surfaces a clean "invalid regular expression" instead of a generic 500
 * — and, critically, never a silent no-op that would widen the slice to the whole portfolio.
 * The client validates the pattern against the same POSIX-ARE subset at build time, so this
 * server code is the backstop for a stale/adversarial client.
 */
export const SEGMENT_REGEX_INVALID_CODE = 'SEGMENT_REGEX_INVALID';

// ── GET /api/portfolio/loans (cursor pagination) ─────────────────────────────

// Readiness/has-transactions slicing rides INSIDE `segment.readiness` (the one
// segment object drives the whole view) — there are deliberately NO standalone
// readiness query flags on this contract.
export const PortfolioLoansQuerySchema = z.object({
  /** Opaque keyset cursor from the previous page's `nextCursor`. */
  cursor: z.string().optional(),
  /** Case-insensitive substring match on the protected (scrambled) loan id. */
  search: z.string().optional(),
  /** Sortable column (default `loan_id`). */
  sort: PortfolioSortKeySchema.optional(),
  /** Sort direction (default `desc`). */
  direction: PortfolioSortDirectionSchema.optional(),
  /**
   * The active portfolio segment, base64url(JSON(Segment)). Optional — absent means the
   * whole portfolio. Rides the query string (segments are small) so the list stays a
   * cacheable GET alongside the cursor; the server decodes + normalizes it.
   */
  segment: z.string().optional(),
});
export type PortfolioLoansQuery = z.infer<typeof PortfolioLoansQuerySchema>;

/** Per-loan modeling readiness across the three modeled outputs. */
export const PortfolioLoanReadinessSchema = z.object({
  creditLoss: z.boolean(),
  payoff: z.boolean(),
  prepayment: z.boolean(),
});
export type PortfolioLoanReadiness = z.infer<typeof PortfolioLoanReadinessSchema>;

/** Terminal state of a loan, from the D2 per-loan summary (`none` = still active). */
export const PortfolioTerminalStatusSchema = z.enum(['none', 'paid_off', 'charged_off']);
export type PortfolioTerminalStatus = z.infer<typeof PortfolioTerminalStatusSchema>;

/** How a loan's effective term was resolved, from the D2 per-loan summary. */
export const PortfolioTermSourceSchema = z.enum(['provided', 'derived_from_maturity', 'none']);
export type PortfolioTermSource = z.infer<typeof PortfolioTermSourceSchema>;

/** F184: provenance of the displayed Original Balance — 'provided' (the
 *  original_loan_amount attribute) vs 'estimated' (the first-observed-balance fallback). */
export const PortfolioOriginalBalanceSourceSchema = z.enum(['provided', 'estimated']);
export type PortfolioOriginalBalanceSource = z.infer<typeof PortfolioOriginalBalanceSourceSchema>;

/**
 * One inventory row — a single modeled loan with its loan-level attributes plus a
 * summary of its observation history, transaction/event activity, and modeling
 * readiness. Money and score fields are strings (Decimal precision preserved);
 * `loanId` is the protected (scrambled) identifier, never a raw loan number.
 */
export const PortfolioLoanRowSchema = z.object({
  loanId: z.string(),
  /** Whether a protected Tax ID is on file for the loan. */
  hasTaxId: z.boolean(),
  // Loan-level attributes (from the origination / snapshot files).
  originalLoanAmount: z.string().nullable(),
  originationDate: z.string().nullable(),
  ficoScore: z.string().nullable(),
  interestRate: z.string().nullable(),
  termMonths: z.string().nullable(),
  // Observation (snapshot) summary.
  currentBalance: z.string().nullable(),
  latestPeriod: z.string().nullable(),
  snapshotCount: z.number().int().nonnegative(),
  // Transaction / event summary.
  transactionCount: z.number().int().nonnegative(),
  /** F123: sign-blind SUM of the loan's transaction amounts (charge-off + recovery + payoff +
   *  prepayment + unscheduled) — a transaction-flow total, NOT a charge-off total (was
   *  `chargeOffTotal`, which mislabelled a mixed-type sum). */
  transactionFlowTotal: z.string().nullable(),
  latestTransactionDate: z.string().nullable(),
  // Per-loan modeling summary (D2): durable, easily-queryable scalars for the loan table.
  /** Original balance at origination (Decimal string). */
  originalBalance: z.string().nullable(),
  /** F184: whether that balance was provided or estimated from the first observation. */
  originalBalanceSource: PortfolioOriginalBalanceSourceSchema,
  /** Effective term in months (direct or derived from a maturity date). */
  effectiveTermMonths: z.number().int().nullable(),
  /** How the effective term was resolved. */
  termSource: PortfolioTermSourceSchema,
  /** Terminal state (`none` = still active). */
  terminalStatus: PortfolioTerminalStatusSchema,
  /** Age in months at the loan's latest observation. */
  ageMonths: z.number().int().nullable(),
  /** Whether the loan is modelable (ageable AND has a positive term). */
  isModeled: z.boolean(),
  // Row-level modeling readiness.
  readiness: PortfolioLoanReadinessSchema,
});
export type PortfolioLoanRow = z.infer<typeof PortfolioLoanRowSchema>;

export const PortfolioLoansResponseSchema = z.object({
  loans: z.array(PortfolioLoanRowSchema),
  /** Opaque cursor for the next page, or null when this is the last page. */
  nextCursor: z.string().nullable(),
  /**
   * Freshness of the org's stored modeling dataset — the SAME server-authoritative signal the
   * Modeling read carries (mirrors `ModelingCurvesResponse.datasetStatus`). When the dataset is not
   * `fresh` the server WITHHOLDS the loan rows (`loans: []`, `nextCursor: null`) rather than serving
   * a partial list over a still-processing portfolio, so a stale client can never render confident-
   * wrong inventory. Clients block on any non-`fresh` value (tolerate unknown → treat as not fresh).
   */
  datasetStatus: ModelingDatasetStatusSchema,
});
export type PortfolioLoansResponse = z.infer<typeof PortfolioLoansResponseSchema>;

export const portfolioLoansContract = {
  method: 'GET',
  path: '/api/portfolio/loans',
  query: PortfolioLoansQuerySchema,
  response: PortfolioLoansResponseSchema,
} as const satisfies ApiContract;

// ── GET /api/portfolio/summary (org-level headline stats) ────────────────────

export const PortfolioSummarySchema = z.object({
  totalLoans: z.number().int().nonnegative(),
  creditLossReadyLoans: z.number().int().nonnegative(),
  payoffReadyLoans: z.number().int().nonnegative(),
  prepaymentReadyLoans: z.number().int().nonnegative(),
  /** Distinct (loan, period) observation pairs — the size of the snapshot history. */
  totalSnapshots: z.number().int().nonnegative(),
  totalTransactions: z.number().int().nonnegative(),
  /**
   * Loans still awaiting a portfolio recompute (the dirty-loan queue, drained
   * incrementally after a large upload or delete). 0 for virtually every read; it
   * is briefly >0 only during a large backfill, while the projection this screen
   * reads is still catching up (the upload's Review is already complete). The
   * Portfolio screen surfaces this as a quiet "updating…" indicator.
   */
  pendingRecomputeCount: z.number().int().nonnegative(),
});
export type PortfolioSummary = z.infer<typeof PortfolioSummarySchema>;

export const PortfolioSummaryResponseSchema = z.object({
  summary: PortfolioSummarySchema,
});
export type PortfolioSummaryResponse = z.infer<typeof PortfolioSummaryResponseSchema>;

export const portfolioSummaryContract = {
  method: 'GET',
  path: '/api/portfolio/summary',
  response: PortfolioSummaryResponseSchema,
} as const satisfies ApiContract;

// ── Segment Builder ──────────────────────────────────────────────────────────

/** One categorical value + its portfolio-wide loan count, for the checklist. */
export const SegmentFieldValueSchema = z.object({
  value: z.string(),
  count: z.number().int().nonnegative(),
});

/**
 * A sliceable field plus its FIXED (whole-portfolio) stats — the panel's control
 * bounds, value list, and ordering. Bounds/value lists are total-portfolio and never
 * shift as the segment narrows.
 */
export const SegmentFieldSchema = z.object({
  key: z.string(),
  displayLabel: z.string(),
  kind: z.enum(['standard', 'custom']),
  valueType: z.enum(['numeric', 'date', 'categorical', 'text']),
  /** Display-only subtype for a numeric field (number/currency/percentage), or null. */
  valueSubtype: FieldValueSubtypeSchema,
  level: z.enum(['loan', 'observation', 'event']),
  sliceRank: z.number().int(),
  /** Numeric/date axis bounds (YYYYMMDD ordinals for date); null when no values. */
  numericMin: z.number().nullable(),
  numericMax: z.number().nullable(),
  distinctCount: z.number().int().nullable(),
  coverageCount: z.number().int().nonnegative(),
  /** Loans with no value for this field (the "include missing/blank" size). */
  blankCount: z.number().int().nonnegative(),
  /** Top-N value buckets for categorical/text; null for numeric/date. */
  values: z.array(SegmentFieldValueSchema).nullable(),
});
export type SegmentField = z.infer<typeof SegmentFieldSchema>;

export const SegmentFieldsResponseSchema = z.object({
  fields: z.array(SegmentFieldSchema),
});
export type SegmentFieldsResponse = z.infer<typeof SegmentFieldsResponseSchema>;

export const portfolioSegmentFieldsContract = {
  method: 'GET',
  path: '/api/portfolio/segment/fields',
  response: SegmentFieldsResponseSchema,
} as const satisfies ApiContract;

// ── F122: server-side value search for a categorical/text field ───────────────
// The fixed field stats carry only the top-200 buckets (TOP_N_VALUES), so a field with
// >200 distinct values (e.g. Branch over 500k loans) can't have its TAIL values found,
// searched, or selected from the stored list. This endpoint searches ALL distinct values
// for one field server-side (indexed EXISTS over the latest-values / attributes tables,
// capped page), so a tail value is findable AND a truncated field's "Select all" can
// resolve the FULL matching set server-side — never an IN() built from the visible 200.
export const SegmentFieldValuesQuerySchema = z.object({
  /** The field key to search (loan-level attribute or observation-level latest value). */
  key: z.string().min(1),
  /** Case-insensitive substring over the field's distinct values. Empty ⇒ the top page. */
  search: z.string().optional(),
});
export type SegmentFieldValuesQuery = z.infer<typeof SegmentFieldValuesQuerySchema>;

export const SegmentFieldValuesResponseSchema = z.object({
  key: z.string(),
  /** Matching distinct values + their portfolio-wide loan counts, capped at `limit`. */
  values: z.array(SegmentFieldValueSchema),
  /** Total distinct values matching the search (≥ `values.length`) — the honest denominator. */
  matchedDistinct: z.number().int().nonnegative(),
  /** True when more matching values exist than this page returned (the client then cannot
   *  enumerate an exact "select all" and must narrow the search — never a truncated IN()). */
  hasMore: z.boolean(),
  /** The page cap applied, so the client can word the "narrow to select all" nudge. */
  limit: z.number().int().positive(),
});
export type SegmentFieldValuesResponse = z.infer<typeof SegmentFieldValuesResponseSchema>;

export const portfolioSegmentFieldValuesContract = {
  method: 'GET',
  path: '/api/portfolio/segment/field-values',
  query: SegmentFieldValuesQuerySchema,
  response: SegmentFieldValuesResponseSchema,
} as const satisfies ApiContract;

export const SegmentCountRequestSchema = z.object({ segment: SegmentSchema });
/**
 * The live count. Failure mode worth knowing: a slice too expensive to evaluate within
 * the shared statement timeout returns the error envelope with
 * {@link SEGMENT_TOO_LARGE_CODE} (HTTP 422) instead of a number — the panel keeps its
 * last good count and offers retry.
 */
export const SegmentCountResponseSchema = z.object({
  /** Loans matching the segment. */
  matched: z.number().int().nonnegative(),
  /** Total loans in the portfolio (the fixed denominator: "matched of total"). */
  total: z.number().int().nonnegative(),
});
export type SegmentCountResponse = z.infer<typeof SegmentCountResponseSchema>;

export const portfolioSegmentCountContract = {
  method: 'POST',
  path: '/api/portfolio/segment/count',
  body: SegmentCountRequestSchema,
  response: SegmentCountResponseSchema,
} as const satisfies ApiContract;

export const SegmentDraftResponseSchema = z.object({ segment: SegmentSchema });
export type SegmentDraftResponse = z.infer<typeof SegmentDraftResponseSchema>;

export const portfolioSegmentContract = {
  method: 'GET',
  path: '/api/portfolio/segment',
  response: SegmentDraftResponseSchema,
} as const satisfies ApiContract;

export const SegmentUpsertRequestSchema = z.object({ segment: SegmentSchema });

export const portfolioSegmentUpsertContract = {
  method: 'PUT',
  path: '/api/portfolio/segment',
  body: SegmentUpsertRequestSchema,
  response: SegmentDraftResponseSchema,
} as const satisfies ApiContract;

// ── Custom Attributes management (list + retype-preview + retype) ─────────────

/**
 * One custom field as the Custom Attributes management screen shows it: identity,
 * its durable type + display subtype, coverage, and a little value context so the
 * user can judge a retype. Only loan-level custom fields are listed (the retypeable
 * set); standard fields and non-loan-level customs are out of scope for this screen.
 */
export const CustomAttributeSchema = z.object({
  key: z.string(),
  displayLabel: z.string(),
  valueType: FieldValueTypeSchema,
  valueSubtype: FieldValueSubtypeSchema,
  /** Loans carrying a value for this field. */
  coverageCount: z.number().int().nonnegative(),
  /** Distinct non-blank values, or null when not computed. */
  distinctCount: z.number().int().nullable(),
  /** Numeric/date axis bounds (YYYYMMDD ordinals for date); null when no values. */
  numericMin: z.number().nullable(),
  numericMax: z.number().nullable(),
  /** A few example values (categorical/text buckets); empty for numeric/date. */
  sampleValues: z.array(z.string()).readonly(),
});
export type CustomAttribute = z.infer<typeof CustomAttributeSchema>;

export const CustomAttributesResponseSchema = z.object({
  fields: z.array(CustomAttributeSchema),
});
export type CustomAttributesResponse = z.infer<typeof CustomAttributesResponseSchema>;

export const portfolioCustomAttributesContract = {
  method: 'GET',
  path: '/api/portfolio/fields',
  response: CustomAttributesResponseSchema,
} as const satisfies ApiContract;

/**
 * Preview a retype before committing: how many of a field's stored values would not
 * fit the proposed type and become empty. `key` + `valueType` ride the query string
 * (a canonical field key can contain characters unsafe in a path segment). Read-only.
 */
export const RetypePreviewQuerySchema = z.object({
  key: z.string(),
  valueType: FieldValueTypeSchema,
});
export const RetypePreviewSchema = z.object({
  /** Non-blank stored values for the field. */
  total: z.number().int().nonnegative(),
  /** How many would not fit the proposed type and become empty. */
  willNull: z.number().int().nonnegative(),
  /** A small sample of the values that would not fit. */
  sample: z.array(z.string()).readonly(),
  /** True when the recompute queue is empty, so the counts are stable. */
  settled: z.boolean(),
  /**
   * Where the counts came from: `live` = exact, computed from the current stored
   * values; `stats` = served from the precomputed whole-portfolio field stats (very
   * large fields), i.e. as of the last data refresh. Clients word the consequence
   * copy accordingly.
   */
  basis: z.enum(['live', 'stats']),
});
export const RetypePreviewResponseSchema = z.object({ preview: RetypePreviewSchema });
export type RetypePreviewResponse = z.infer<typeof RetypePreviewResponseSchema>;

export const portfolioRetypePreviewContract = {
  method: 'GET',
  path: '/api/portfolio/fields/retype-preview',
  query: RetypePreviewQuerySchema,
  response: RetypePreviewResponseSchema,
} as const satisfies ApiContract;

/**
 * Retype one or more custom fields atomically. A single batch (the Custom Attributes
 * save, or a single change from the mapping-screen modal) commits all registry
 * updates + dirty-loan enqueues in one transaction, then the projection is rebuilt.
 * `valueType`/`valueSubtype` are the new durable type + display subtype (subtype is
 * display-only; a subtype-only change skips re-projection).
 */
export const RetypeChangeSchema = z.object({
  key: z.string(),
  valueType: FieldValueTypeSchema,
  valueSubtype: FieldValueSubtypeSchema,
});
export const RetypeRequestSchema = z
  .object({
    changes: z.array(RetypeChangeSchema).min(1),
  })
  // One change per key per batch: duplicate keys in a single request are ambiguous
  // (which type wins) and would double-process the same field, so reject them.
  .superRefine((req, ctx) => {
    if (new Set(req.changes.map((c) => c.key)).size !== req.changes.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Duplicate field key in a single retype batch.',
        path: ['changes'],
      });
    }
  });
export const RetypeResultSchema = z.object({
  key: z.string(),
  outcome: z.enum(['noop', 'subtype_only', 'type_changed']),
  enqueuedLoanCount: z.number().int().nonnegative(),
});
export const RetypeResponseSchema = z.object({
  results: z.array(RetypeResultSchema),
});
export type RetypeRequest = z.infer<typeof RetypeRequestSchema>;
export type RetypeResponse = z.infer<typeof RetypeResponseSchema>;

export const portfolioRetypeContract = {
  method: 'POST',
  path: '/api/portfolio/fields/retype',
  body: RetypeRequestSchema,
  response: RetypeResponseSchema,
} as const satisfies ApiContract;

/**
 * Batch-edit the org's value classifications for value-classifiable Standard fields
 * (status fields loan_status / closure_reason / charge_off_status; flag fields
 * charge_off_flag / payoff_flag / prepayment_flag). The Fields page's Standard-fields
 * editor saves the org's stored vocabulary here; the server validates each
 * classification against its field's family (status → paid_off/charged_off/active;
 * flag → event/not_event) and upserts idempotently. When at least ONE row actually
 * changes (a new value or a re-classified one), the whole org's modeling facts are
 * marked stale for a DERIVE-ONLY re-derive (observations unchanged) and the backfill
 * is kicked; an idempotent re-save of the same vocabulary changes nothing.
 */
export const ValueClassificationChangeSchema = z.object({
  fieldKey: z.enum(VALUE_CLASSIFIABLE_FIELD_KEYS),
  /** The raw value as uploaded; stored normalized (lower + trim) by the server. */
  value: z.string().min(1),
  classification: z.enum(VALUE_CLASSIFICATIONS),
});
export type ValueClassificationChange = z.infer<typeof ValueClassificationChangeSchema>;

export const ValueClassificationsRequestSchema = z.object({
  changes: z.array(ValueClassificationChangeSchema).min(1),
});
export type ValueClassificationsRequest = z.infer<typeof ValueClassificationsRequestSchema>;

export const ValueClassificationsResponseSchema = z.object({
  /** Rows that actually changed (inserted or re-classified). 0 → idempotent re-save. */
  changedCount: z.number().int().nonnegative(),
  /** True when the change triggered the org-wide derive-only re-derive. */
  rederiveTriggered: z.boolean(),
});
export type ValueClassificationsResponse = z.infer<typeof ValueClassificationsResponseSchema>;

export const portfolioFieldClassificationsContract = {
  method: 'POST',
  path: '/api/portfolio/fields/classifications',
  body: ValueClassificationsRequestSchema,
  response: ValueClassificationsResponseSchema,
} as const satisfies ApiContract;

// ── Standard-fields editor (Fields page → "how we read your fields") ──────────

/**
 * The two percent display variants (a percentConfigurable field's org-scoped choice).
 * The inferred type equals the shared `PercentSubtype` (uploader) by construction — the
 * one canonical `PercentSubtype` type name is re-exported from the uploader barrel.
 */
export const PercentSubtypeSchema = z.enum(['percentage', 'percent_points']);

/**
 * One classifiable STANDARD field as the Fields-page Standard editor shows it: its
 * family + family vocabulary (the selectable classifications), and the org's already
 * stored per-value classifications. The stored vocabulary is complete for all accepted
 * data — the mapping gate forces every distinct value through classification — so this
 * is served from `portfolio_value_classifications`, never an observation scan
 * (scale-safe). Each stored value carries the NORMALIZED (lower + trim) form the derive
 * joins on.
 */
export const StandardClassifiableFieldSchema = z.object({
  fieldKey: z.enum(VALUE_CLASSIFIABLE_FIELD_KEYS),
  family: z.enum(['status', 'flag']),
  /** The family's classification vocabulary — the select options for every value. */
  options: z.array(z.enum(VALUE_CLASSIFICATIONS)).readonly(),
  /** The org's stored classification per normalized value, most-common ordering by the server. */
  vocabulary: z
    .array(z.object({ value: z.string(), classification: z.enum(VALUE_CLASSIFICATIONS) }))
    .readonly(),
});
export type StandardClassifiableField = z.infer<typeof StandardClassifiableFieldSchema>;

/**
 * One percentConfigurable STANDARD field as the editor shows it: its resolved percent
 * variant (the org's override when set, else the catalog default) — the editable
 * control's current value.
 */
export const StandardPercentFieldSchema = z.object({
  fieldKey: z.string(),
  label: z.string(),
  variant: PercentSubtypeSchema,
});
export type StandardPercentField = z.infer<typeof StandardPercentFieldSchema>;

export const StandardFieldsResponseSchema = z.object({
  classifiableFields: z.array(StandardClassifiableFieldSchema),
  percentFields: z.array(StandardPercentFieldSchema),
});
export type StandardFieldsResponse = z.infer<typeof StandardFieldsResponseSchema>;

/** Read the org's editable standard-field interpretation (vocabulary + percent variant). */
export const portfolioStandardFieldsContract = {
  method: 'GET',
  path: '/api/portfolio/fields/standard',
  response: StandardFieldsResponseSchema,
} as const satisfies ApiContract;

/**
 * Set the org's percent variant for a percentConfigurable standard field (today only
 * `interest_rate`). When the choice crosses the percent boundary the stored values are
 * re-scaled at projection, so the field's loans re-project (dirty enqueue → recompute →
 * re-derive → D4), exactly like a custom retype's percent-scaling change. `requireEditAccess`.
 */
export const StandardPercentVariantRequestSchema = z.object({
  fieldKey: z.string(),
  variant: PercentSubtypeSchema,
});
export type StandardPercentVariantRequest = z.infer<typeof StandardPercentVariantRequestSchema>;

export const StandardPercentVariantResponseSchema = z.object({
  /** True when the variant actually changed (false on an idempotent re-save). */
  changed: z.boolean(),
  /** True when the change re-scaled stored values and re-projected the field's loans. */
  reprojected: z.boolean(),
  /** Loans newly enqueued for re-projection (0 on a no-op / display-only change). */
  enqueuedLoanCount: z.number().int().nonnegative(),
});
export type StandardPercentVariantResponse = z.infer<typeof StandardPercentVariantResponseSchema>;

export const portfolioStandardPercentVariantContract = {
  method: 'POST',
  path: '/api/portfolio/fields/standard-subtype',
  body: StandardPercentVariantRequestSchema,
  response: StandardPercentVariantResponseSchema,
} as const satisfies ApiContract;
